// Smart Sash Admin Panel - Stream Store
import {
  ActiveSessionInfo,
  ActiveSessionsParams,
  SessionAccessInfo,
  SessionConnectInfo,
} from '@/models/teaching.models';
import { teachingOrchestrationService } from '@/services/grpc/teachingOrchestrationService';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface StreamState {
  sessions: ActiveSessionInfo[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  isLoading: boolean;
  error: string | null;

  activeSession: ActiveSessionInfo | null;
  connectionInfo: SessionConnectInfo | null;
  isJoining: boolean;

  accessChecks: Record<string, SessionAccessInfo>;

  fetchSessions: (params: ActiveSessionsParams) => Promise<void>;
  joinSession: (
    institutionId: string,
    sessionId: string,
    deviceId: string,
    userId: string
  ) => Promise<SessionConnectInfo>;
  leaveSession: (institutionId: string, sessionId: string, userId: string) => Promise<void>;
  checkAccess: (
    institutionId: string,
    userId: string,
    deviceId: string
  ) => Promise<SessionAccessInfo>;
  setActiveSession: (session: ActiveSessionInfo | null) => void;
  clearConnectionInfo: () => void;
  clearError: () => void;
}

export const useStreamStore = create<StreamState>()(
  devtools(
    (set) => ({
      sessions: [],
      totalCount: 0,
      totalPages: 0,
      currentPage: 0,
      isLoading: false,
      error: null,

      activeSession: null,
      connectionInfo: null,
      isJoining: false,

      accessChecks: {},

      fetchSessions: async (params: ActiveSessionsParams) => {
        set({ isLoading: true, error: null });
        try {
          const response = await teachingOrchestrationService.getActiveSessions(params);
          set({
            sessions: response.sessions,
            totalCount: response.totalCount,
            totalPages: response.totalPages,
            currentPage: response.currentPage,
            isLoading: false,
          });
        } catch (error: unknown) {
          set({
            error: getErrorMessage(error, 'Failed to fetch active sessions'),
            isLoading: false,
          });
          throw error;
        }
      },

      joinSession: async (
        institutionId: string,
        sessionId: string,
        deviceId: string,
        userId: string
      ) => {
        set({ isJoining: true, error: null });
        try {
          const connectionInfo = await teachingOrchestrationService.joinSession(
            institutionId,
            sessionId,
            deviceId,
            userId
          );
          if (!connectionInfo.success) {
            throw new Error(connectionInfo.errorMessage || 'Failed to join session');
          }
          set({ connectionInfo, isJoining: false });
          return connectionInfo;
        } catch (error: unknown) {
          set({
            error: getErrorMessage(error, 'Failed to join session'),
            isJoining: false,
          });
          throw error;
        }
      },

      leaveSession: async (institutionId: string, sessionId: string, userId: string) => {
        try {
          await teachingOrchestrationService.leaveSession(institutionId, sessionId, userId);
        } catch (error: unknown) {
          console.error('Failed to leave session:', error);
        } finally {
          set({ activeSession: null, connectionInfo: null });
        }
      },

      checkAccess: async (institutionId: string, userId: string, deviceId: string) => {
        try {
          const accessInfo = await teachingOrchestrationService.checkAccess(
            institutionId,
            userId,
            deviceId
          );
          set((state) => ({
            accessChecks: { ...state.accessChecks, [deviceId]: accessInfo },
          }));
          return accessInfo;
        } catch (error: unknown) {
          const denied: SessionAccessInfo = {
            canJoin: false,
            canPublish: false,
            canAdminStop: false,
            reason: getErrorMessage(error, 'Access check failed'),
          };
          set((state) => ({
            accessChecks: { ...state.accessChecks, [deviceId]: denied },
          }));
          return denied;
        }
      },

      setActiveSession: (session: ActiveSessionInfo | null) => set({ activeSession: session }),
      clearConnectionInfo: () => set({ connectionInfo: null }),
      clearError: () => set({ error: null }),
    }),
    { name: 'StreamStore' }
  )
);
